import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Spinner } from "react-bootstrap";
import { TextField } from "@mui/material";
import { EditButton } from "components/Buttons/EditButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import SaveCancelButtons from "components/Buttons/SaveCancelButtons";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import { toast } from "react-toastify";
import ViewBagsModal from "../CreateGRN/ViewBagsModal";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";

interface RowData {
  dispatch_item_id: string;
  item_id?: string;
  item_name: string;
  item_code: string;
  category_name: string;
  category_id?: string;
  sub_category_name: string;
  sub_category_id?: string;
  unit: any;
  unit_id: string;
  RowData: any;
  bags: any;
  dispatch_quantity: number;
  return_quantity: number;
  remark: string;
  master_item?: any;
}

interface PropsTypes {
  onItemsUpdated?: (items: RowData[]) => void;
  initialItems?: RowData[]; // For edit mode
  isEdit?: boolean;
}

const LabourReturnedItems = ({
  onItemsUpdated,
  initialItems,
  isEdit = true,
}: PropsTypes) => {
  const [rows, setRows] = useState<RowData[]>([]);
  const [deletingRows, setDeletingRows] = useState<string[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingRowData, setEditingRowData] = useState<RowData | null>(null);
  const [editErrors, setEditErrors] = useState<{
    return_quantity?: string;
  }>({});
  // const [showPreview, setShowPreview] = useState<boolean>(false);
  const [showBagsModal, setShowBagsModal] = useState<boolean>(false);
  const [selectedItemBags, setSelectedItemBags] = useState<any[]>([]);
  const [selectedItemName, setSelectedItemName] = useState<string>("");

  useEffect(() => {
    console.log("Initial Items2", initialItems);
    console.log("Rows", rows);
  }, [initialItems, rows]);

  useEffect(() => {
    if (!initialItems) return;

    setRows(initialItems); // set rows
    setEditingRowId(null); // RESET edit mode
    setEditingRowData(null); // RESET edit values
    setEditErrors({}); // RESET errors
    setShowBagsModal(false); // RESET modal
    setSelectedItemBags([]); // RESET bags
    setSelectedItemName(""); // RESET modal item name

    if (onItemsUpdated) {
      onItemsUpdated(initialItems);
    }
  }, [initialItems]);

  const handleDelete = (id: string): void => {
    // Trigger animation
    setDeletingRows((prev) => [...prev, id]);

    setTimeout(() => {
      setRows((prev) => {
        const updated = prev.filter((r) => r.dispatch_item_id !== id);

        // Push updated rows up to parent
        if (updated && onItemsUpdated) {
          onItemsUpdated(updated);
        }

        return updated;
      });

      // Clear animation state
      setDeletingRows((prev) => prev.filter((x) => x !== id));

      // Show success toast
      toast.success("Item deleted successfully");
    }, 300);
  };

  const handleEdit = (row: RowData): void => {
    setEditingRowId(row.dispatch_item_id);
    setEditingRowData({ ...row });
    setEditErrors({}); // Clear any previous errors when starting to edit
  };

  const handleSaveEdit = (): void => {
    if (!editingRowData) return;

    // Validate return_quantity
    if (
      !editingRowData.return_quantity ||
      editingRowData.return_quantity === undefined ||
      editingRowData.return_quantity === null ||
      editingRowData.return_quantity < 0
    ) {
      toast.warning(
        "Return Quantity is required and must be greater than or equal to 0"
      );
      setEditErrors((prev) => ({
        ...prev,
        return_quantity:
          "Return Quantity is required and must be greater than or equal to 0",
      }));
      return;
    }

    // Validate return_quantity cannot be greater than dispatch_quantity
    if (editingRowData.return_quantity > editingRowData.dispatch_quantity) {
      toast.warning(
        "Return Quantity cannot be greater than Issue Quantity"
      );
      setEditErrors((prev) => ({
        ...prev,
        return_quantity:
          "Return Quantity cannot be greater than Issue Quantity",
      }));
      return;
    }

    setRows((prev) => {
      const updated = prev.map((r) =>
        r.dispatch_item_id === editingRowId ? editingRowData : r
      );

      // Push updated rows up to parent
      if (onItemsUpdated) {
        onItemsUpdated(updated);
      }

      return updated;
    });

    setEditingRowId(null);
    setEditingRowData(null);
    setEditErrors({}); // Clear errors when saving

    // Show success toast
    toast.success("Item updated successfully");
  };

  const handleCancelEdit = (): void => {
    setEditingRowId(null);
    setEditingRowData(null);
    setEditErrors({}); // Clear errors when canceling edit
    toast.warning("Item update cancelled");
  };

  const handleEditFieldChange = (
    key: keyof RowData,
    value: string | number
  ): void => {
    if (!editingRowData) return;

    const numValue = typeof value === "string" ? parseFloat(value) : value;

    // Validate negative values
    if (key === "return_quantity" && !isNaN(numValue) && numValue < 0) {
      const errorMessage = "Return Quantity cannot be negative";

      setEditErrors((prev) => ({ ...prev, [key]: errorMessage }));
      toast.error(errorMessage);
      return; // Don't update the value if it's negative
    }

    // Validate return_quantity cannot be greater than dispatch_quantity
    if (
      key === "return_quantity" &&
      !isNaN(numValue) &&
      numValue > editingRowData.dispatch_quantity
    ) {
      const errorMessage = "Return Quantity cannot be greater than Issue Quantity";

      setEditErrors((prev) => ({ ...prev, [key]: errorMessage }));
      toast.error(errorMessage);
      return; // Don't update the value if it exceeds dispatch_quantity
    }

    // Clear error if value is valid
    if (editErrors[key as keyof typeof editErrors]) {
      setEditErrors((prev) => {
        const updated = { ...prev };
        delete updated[key as keyof typeof editErrors];
        return updated;
      });
    }

    const updated = { ...editingRowData, [key]: value };
    setEditingRowData(updated);
  };
  console.log("Rows in Labour Returned Items:", rows);
  return (
    <Container fluid className="mx-auto mt-4">
      <Row className="align-items-center mb-4">
        <Col>
          <h6 className="card-title mb-0">Labour Returned Items</h6>
        </Col>
      </Row>

      <style>
        {`
          .sticky-column {
            position: relative;
          }
          .sticky-column::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 1px;
            background-color: #dee2e6;
            z-index: 12;
          }
          thead .sticky-column::before {
            z-index: 13;
          }
        `}
      </style>
      <Table hover responsive className="align-middle text-center">
        <thead>
          <tr>
            <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>
              Sr No.
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
              Category
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
              Sub Category
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
              Item Name
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
              Item Code
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "100px" }}>
              Unit
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
              Bag No
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
              Issue Quantity
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "180px" }}>
              Return Quantity
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "200px",
                maxWidth: "250px",
              }}
            >
              Remark
            </th>
            <th
              className="sticky-column"
              style={{
                ...tableStyles.headerAlt,
                ...tableStyles.actionHeader,
                minWidth: "100px",
              }}
            >
              Action
            </th>
          </tr>
        </thead>

        <tbody>
          {/* Existing Rows */}
          {rows.map((row:any, idx) => {
            const isEditing = editingRowId === row.dispatch_item_id;
            const displayData =
              isEditing && editingRowData ? editingRowData : row;

            return (
              <tr
                key={row.dispatch_item_id}
                style={isEditing ? tableStyles.rowEditing : {}}
              >
                <td style={tableStyles.cell}>{idx + 1}</td>

                {/* Category */}
                <td style={tableStyles.cell}>
                  <span>{displayData.category_name || ""}</span>
                </td>

                {/* Sub Category */}
                <td style={tableStyles.cell}>
                  <span>{displayData.sub_category_name || ""}</span>
                </td>

                {/* Item Name */}
                <td style={tableStyles.cell}>
                  <span>{displayData.item_name || ""}</span>
                </td>

                {/* Item Code */}
                <td style={tableStyles.cell}>
                  <span>{displayData.item_code || ""}</span>
                </td>

                {/* Unit */}
                <td style={tableStyles.cell}>
                  <span>{displayData?.unit?.unit_name || displayData?.master_item?.unit?.unit_name || "-"}</span>
                </td>

                {/* Bag No. */}

                <td style={tableStyles.cell}>
                  {isEdit ? (
                    row?.bags && row.bags.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedItemBags(row?.bags || []);
                          setSelectedItemName(row?.item_name || "");
                          setShowBagsModal(true);
                        }}
                        style={{
                          color: "blue",
                          cursor: "pointer",
                          background: "none",
                          border: "none",
                          padding: 0,
                          textDecoration: "underline",
                        }}
                      >
                        View Bags ({row.bags.length})
                      </button>
                    ) : (
                      "-"
                    )
                  ) : (
                    row?.bag_no || "-"
                  )}
                </td>

                {/* Bag Details Modal */}
                <ViewBagsModal
                  show={showBagsModal}
                  handleClose={() => {
                    setShowBagsModal(false);
                    setSelectedItemBags([]);
                    setSelectedItemName("");
                  }}
                  bags={selectedItemBags}
                  itemName={selectedItemName}
                />

                {/* Issue Quantity */}
                <td style={tableStyles.cell}>
                  <span>{displayData.dispatch_quantity || 0}</span>
                </td>

                {/* Return Quantity */}
                <td style={tableStyles.cell}>
                  {isEditing ? (
                    <TextField
                      size="small"
                      type="number"
                      value={displayData.return_quantity || 0}
                      onChange={(e) =>
                        handleEditFieldChange(
                          "return_quantity",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      inputProps={{ 
                        min: 0,
                        max: displayData.dispatch_quantity || 0
                      }}
                      fullWidth
                      error={!!editErrors.return_quantity}
                      helperText={editErrors.return_quantity}
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  ) : (
                    <span>{row.return_quantity || 0}</span>
                  )}
                </td>

                {/* Remarks */}
                <td
                  style={{
                    ...tableStyles.cell,
                    maxWidth: "250px",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  }}
                >
                  <TruncatedTextCell
                    text={displayData.remark}
                    maxWidth="300px"
                    maxLines={4}
                    rowIndex={idx}
                    fallbackText="—"
                  />
                </td>

                {/* Actions */}
                <td
                  style={
                    isEditing
                      ? tableStyles.actionCellEditing
                      : tableStyles.actionCell
                  }
                  className="sticky-column"
                >
                  {isEditing ? (
                    <SaveCancelButtons
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                      containerStyle={tableStyles.actionButtonContainer}
                    />
                  ) : (
                    <div style={tableStyles.actionButtonContainer}>
                      <EditButton
                        onClick={() => handleEdit(row)}
                        iconClassName="ph-pencil"
                      />
                      {/* <DeleteButton
                        onClick={() => handleDelete(row.dispatch_item_id)}
                        iconClassName="ph-trash"
                        isLoading={deletingRows.includes(row.dispatch_item_id)}
                      >
                        {deletingRows.includes(row.dispatch_item_id) && (
                          <Spinner animation="border" size="sm" />
                        )}
                      </DeleteButton> */}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
};

export default LabourReturnedItems;
